import { Divider, Stack } from '@mui/material'
import { useGetMilkingTableData } from 'hook/query/useGetMilkYieldTableData'
import { useDebounce } from 'hook/useDebounce'
import { useUndigitizedCowKeys } from './hooks/useUndigitizedCowKeys'
import moment from 'moment'
import { useCallback, useMemo, useState } from 'react'
import { CardStandart } from 'ui/CardStandart'
import CustomPagination from 'ui/CustomPagination'
import TableTabs from './components/TableTabs'
import YieldTable from './components/YieldTable'
import { PAGE_SIZE } from './constans'
import { useParamsTracker } from './hooks/useParamsTracker'
import { ShiftType, SortOrderings } from './types'

const currentDate = moment().format('YYYY-MM-DD')

interface MilkYeildTableProps {
	selectedBarn: number | null
	selectedGroup: number[]
}

function MilkYeildTable({ selectedBarn, selectedGroup }: MilkYeildTableProps) {
	const [debouncedValue, handleChange, value] = useDebounce()
	const [selectedDate, setSelectedDate] = useState(currentDate)
	const [sortOrder, setSortOrder] = useState<SortOrderings | null>(null)
	const [page, setPage] = useState(1)
	const [selectedTags, setSelectedTags] = useState<number[]>([])
	const [selectedShift, setSelectedShift] = useState<ShiftType>(ShiftType.ALL)

	const formattedDate = useMemo(() => {
		return moment(selectedDate).format('YYYY-MM-DD')
	}, [selectedDate])

	const paramsToTrack = useMemo(
		() => ({
			selectedDate: formattedDate,
			selectedBarn,
			selectedGroup,
			selectedTags,
			selectedShift,
			sortOrder,
			tag_id_search: debouncedValue,
		}),
		[
			formattedDate,
			selectedBarn,
			selectedGroup,
			selectedTags,
			selectedShift,
			sortOrder,
			debouncedValue,
		],
	)

	const { shouldResetPagination } = useParamsTracker({
		params: paramsToTrack,
		onParamsChange: () => setPage(1),
	})
	const { list, totalCount, isLoading } = useGetMilkingTableData({
		date: formattedDate,
		barn_id: selectedBarn ?? undefined,
		...(selectedGroup.length > 0 && { group_id: selectedGroup.join(',') }),
		page,
		page_size: PAGE_SIZE,
		period: selectedShift === ShiftType.ALL ? undefined : selectedShift,
		tag_id: selectedTags,
		ordering: sortOrder,
		tag_id_search: debouncedValue,
	})
	const needsUndigitizedCatalog =
		list.length > 0 && list.every(item => typeof item.identified !== 'boolean')
	const { data: undigitizedKeys } = useUndigitizedCowKeys(
		needsUndigitizedCatalog,
	)

	const handleSortOrder = useCallback(order => {
		setSortOrder(prev => (prev === order ? null : order))
	}, [])

	return (
		<CardStandart sx={{ padding: '24px' }}>
			<Stack gap='24px'>
				<TableTabs
					data={list}
					selectedBarn={selectedBarn}
					selectedGroup={selectedGroup}
					selectedTags={selectedTags}
					setSelectedTags={setSelectedTags}
					selectedDate={selectedDate}
					setSelectedDate={setSelectedDate}
					setSelectedShift={setSelectedShift}
					selectedShift={selectedShift}
					onChangeInput={handleChange}
					inputValue={value}
				/>
				<YieldTable
					data={list}
					isLoading={isLoading}
					sortOrder={sortOrder}
					setSortOrder={handleSortOrder}
					undigitizedTags={undigitizedKeys?.tags}
					undigitizedCollars={undigitizedKeys?.collars}
				/>
				<Divider />

				<CustomPagination
					onChangeFromServer={setPage}
					count={PAGE_SIZE}
					isResetPage={shouldResetPagination}
					totalCount={totalCount}
					sx={{ mt: '16px' }}
					disabled={isLoading}
				/>
			</Stack>
		</CardStandart>
	)
}

export default MilkYeildTable
